import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config/config';

// Helper function to create authenticated fetch request
const fetchWithAuth = async (url, options = {}) => {
  // Get the token from localStorage
  const token = localStorage.getItem('token');
  
  // Set up headers with authentication
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers
  };

  const response = await fetch(url, {
    ...options,
    headers
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error occurred' }));
    const error = new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
    error.response = response;
    error.data = errorData;
    throw error;
  }

  return response.json();
};

// API base URL
const API_URL = `${config.apiUrl}/api`;

// Async thunks
export const fetchClients = createAsyncThunk(
  'clients/fetchClients',
  async (params = {}, { rejectWithValue }) => {
    try {
      // Build query string from params
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value);
        }
      });
      
      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      
      const response = await fetchWithAuth(`${API_URL}/clients${queryString}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch clients');
    }
  }
);

export const fetchClientById = createAsyncThunk(
  'clients/fetchClientById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/clients/${id}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch client');
    }
  }
);

export const createClient = createAsyncThunk(
  'clients/createClient',
  async (clientData, { rejectWithValue }) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/clients`, {
        method: 'POST',
        body: JSON.stringify(clientData)
      });
      
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create client');
    }
  }
);

export const updateClient = createAsyncThunk(
  'clients/updateClient',
  async ({ id, updates }, { rejectWithValue }) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/clients/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update client');
    }
  }
);

export const deleteClient = createAsyncThunk(
  'clients/deleteClient',
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/clients/${id}`, {
        method: 'DELETE'
      });
      
      return { id, data: response };
    } catch (error) {
      return rejectWithValue(error.data || {
        message: error.message || 'Failed to delete client'
      });
    }
  }
);

// Load column preferences
export const loadColumnPreferences = createAsyncThunk(
  'clients/loadColumnPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/users/preferences?type=clients`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load preferences');
    }
  }
);

// Save column preferences
export const saveColumnPreferences = createAsyncThunk(
  'clients/saveColumnPreferences',
  async ({ columns, type }, { rejectWithValue }) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/users/preferences/columns`, {
        method: 'PUT',
        body: JSON.stringify({ 
          columns,
          type
        })
      });
      
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to save preferences');
    }
  }
);

const initialState = {
  clients: [],
  client: null,
  loading: false,
  error: null,
  associationError: null,
  pagination: {
    total: 0,
    page: 1,
    pages: 0
  },
  columnPreferences: []
};

const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetClientState: (state) => {
      state.client = null;
    },
    clearAssociationError: (state) => {
      state.associationError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch clients
      .addCase(fetchClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch clients';
      })
      
      // Fetch client by ID
      .addCase(fetchClientById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClientById.fulfilled, (state, action) => {
        state.loading = false;
        state.client = action.payload.data;
      })
      .addCase(fetchClientById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch client';
      })
      
      // Create client
      .addCase(createClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createClient.fulfilled, (state, action) => {
        state.loading = false;
        state.clients.unshift(action.payload.data);
        if (state.pagination) {
          state.pagination.total += 1;
        }
      })
      .addCase(createClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create client';
      })
      
      // Update client
      .addCase(updateClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateClient.fulfilled, (state, action) => {
        state.loading = false;
        const updatedClient = action.payload.data;
        // Update in clients array
        const index = state.clients.findIndex(c => c._id === updatedClient._id);
        if (index !== -1) {
          state.clients[index] = updatedClient;
        }
        // Update client detail if it's the current view
        if (state.client && state.client._id === updatedClient._id) {
          state.client = updatedClient;
        }
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update client';
      })
      
      // Delete client
      .addCase(deleteClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteClient.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = state.clients.filter(client => client._id !== action.payload.id);
        if (state.pagination) {
          state.pagination.total -= 1;
        }
        if (state.client && state.client._id === action.payload.id) {
          state.client = null;
        }
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { message: 'Failed to delete client' };
        // Check if this is an association error
        if (action.payload?.hasAssociations) {
          state.associationError = {
            ...action.payload,
            clientId: action.meta.arg // Save the client ID
          };
        }
      })
      
      // Load column preferences
      .addCase(loadColumnPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadColumnPreferences.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.clientColumns) {
          state.columnPreferences = action.payload.clientColumns;
        } else if (action.payload.preferences?.clientColumns) {
          state.columnPreferences = action.payload.preferences.clientColumns;
        }
      })
      .addCase(loadColumnPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load column preferences';
      })
      
      // Save column preferences
      .addCase(saveColumnPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveColumnPreferences.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.columns) {
          state.columnPreferences = action.payload.columns;
        }
      })
      .addCase(saveColumnPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to save column preferences';
      });
  }
});

export const { clearError, resetClientState, clearAssociationError } = clientsSlice.actions;
export default clientsSlice.reducer; 